import _ from 'lodash';
import { getHistory, getLocaleFreePathname } from 'app/history';
import objectToQueryString from 'mz-utils/objectToQueryString';
import { call, select } from 'redux-saga/effects';
import { handleRefParam } from 'app/sagas/utils';
import {
  getActiveLanguage,
  getCurrency,
  getLocation,
  getLoggedIn,
  getSavedSearch,
  behavesAsTravelAgent,
  isBookByCustomer,
  isTravelAgentCredits,
} from 'app/sagas/selectors';
import config from 'config';

const languageToOldFormat = (language) =>
  language?.replace(/(_.*$)/, (match) => match.replace('_', '-').toLowerCase());

const allowedParams = [
  'ta',
  'wl',
  'staging_api',
  'testing_api',
  'gid',
  'guid',
  'airline',
  'airline2',
  'flight_number',
  'flight_number2',
  'customer_special_instructions',
  'partner_tracking_id',
  'coupon_code',
  'first_name',
  'last_name',
  'phone_number',
  'email',
  'dial_code',
  'utm_source',
  'branch',
  'utm_campaign',
  'campaign',
];

export default function* doBookTrip({
  payload: {
    session_id,
    trip_id,
    ondemand,
    selectedTicketId,
    selectedScheduleIndex,
    returnSelectedTicketId,
    returnSelectedScheduleIndex,
    fromSearchResults,
  },
}) {
  const currency = yield select(getCurrency);
  const location = yield select(getLocation);
  const ref = yield call(handleRefParam);
  const query = _.pick(location.query, allowedParams);
  const isLoggedin = yield select(getLoggedIn);
  const activeLanguage = yield select(getActiveLanguage);
  const bookByCustomer = yield select(isBookByCustomer);
  const travelAgentCredits = yield select(isTravelAgentCredits);

  const behavesAsTA = yield select(behavesAsTravelAgent);

  let params = { session_id, trip_id, currency, ...query, ...ref };
  if (ondemand) params.ondemand = 1;

  const { customer_id } = yield select(getSavedSearch);
  if (customer_id) {
    params = { ...params, customer_id };
  }

  // In our spagetti sagas code we call `doBookTrip` twise
  // when the user click to the "book" button on the search results,
  // which push to history "/book" page two times. In that case, when the
  // user click "back" button, he won't go to the search results page
  // To DIRTY fix that, we use "replace" when the page is already "/book"
  const history = yield call(getHistory);
  const pathname = getLocaleFreePathname(history.location.pathname);

  const activeLanguageOldFormat = languageToOldFormat(activeLanguage);
  const isUserGuest = !isLoggedin;
  const isLoyaltyProgramDisabled = !config.LOYALTY_PROGRAM_ENABLED;
  const isCustomBookingDomainDisabled = !config.CUSTOM_BOOKING_DOMAIN;
  const isNotAppStaging =
    window.location.origin !== 'https://app-staging.mozio.com';

  // NOTE: White labels that should NOT redirect to new checkout (temporary)
  const excludedWhiteLabels = [
    'tui',
    'tuitxc',
    'tui-german',
    'tuinordic',
    'tui-team',
    'tui-may',
    'tui-qat',
    'tui-ch-app',
    'tuiservicereps',
    'tuiheathrowexpress',
    'tuiagent',
    'tuiagentnlcruise',
    'webjet',
    'webjetstaff',
    'thrillophilia',
    'thrillophiliasales',
    'wakanownetrate',
    'wo-targetprice-10discount',
    // Not a partner — the e2e suite's own WL, asserted by E2E-AGT-08 (DEV-12801).
    'mozio-e2e-excluded',
  ];
  const isWhiteLabelExcluded = excludedWhiteLabels.includes(config.PARTNER_REF);

  // Exclude testing environment from new checkout redirect
  const isNotTestingEnvironment =
    process.env.CONFIG_ENV !== 'testing' && params.ref !== 'mozio-fe-dev-tests';

  // Only redirect to new checkout for round_trip or one_way modes
  const isValidMode =
    location.query?.mode === 'one_way' || location.query?.mode === 'round_trip';

  // Only redirect when the booking was initiated from the search results
  // page. Prevents the legacy (v1) checkout page from re-triggering the
  // redirect on load, when `loadBookTrip` re-dispatches `doBookTrip`.
  const isFromSearchResults = !!fromSearchResults;

  const conditions = {
    isFromSearchResults,
    isNotTA: isUserGuest || !behavesAsTA,
    isLoyaltyProgramDisabled,
    isCustomBookingDomainDisabled,
    isWhiteLabelNotExcluded: !isWhiteLabelExcluded,
    isNotTestingEnvironment,
    isNotAppStaging,
    isValidMode,
  };

  const conditionsTravelAgent = {
    isFromSearchResults,
    isLoyaltyProgramDisabled,
    isCustomBookingDomainDisabled,
    isLoggedin: isLoggedin && behavesAsTA,
    isWhiteLabelNotExcluded: !isWhiteLabelExcluded,
    bookByUser: !behavesAsTA || (behavesAsTA && !bookByCustomer),
    noTravelAgentCredits: !travelAgentCredits,
    isNotTestingEnvironment,
    isNotAppStaging,
    isValidMode,
  };

  const shouldRedirectToNewCheckout = (conditions) =>
    Object.values(conditions).every(Boolean);

  const redirectToNewCheckout = () => {
    const queryParams = {
      session_id,
      trip_id,
      currency,
      from_path: encodeURIComponent(btoa(window.location.href)),
      ...query,
      ...params,
      ...(selectedTicketId != null && { selectedTicketId }),
      ...(selectedScheduleIndex != null && { selectedScheduleIndex }),
      ...(returnSelectedTicketId != null && { returnSelectedTicketId }),
      ...(returnSelectedScheduleIndex != null && {
        returnSelectedScheduleIndex,
      }),
    };

    const queryString = Object.entries(queryParams)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      )
      .join('&');

    const newCheckoutUrl = `/v2/${activeLanguageOldFormat}/book?${queryString}`;
    window.location.href = newCheckoutUrl;
  };

  if (shouldRedirectToNewCheckout(conditions)) {
    yield call(redirectToNewCheckout);
    return;
  }

  if (shouldRedirectToNewCheckout(conditionsTravelAgent)) {
    yield call(redirectToNewCheckout);
    return;
  }

  const updateFunc = pathname === '/book' ? history.replace : history.push;
  yield call(updateFunc, `/book${objectToQueryString(params)}`);
}
